/**
 * The AI assistant button.
 *
 * The mark and the motion are ported from Conduit CRM's `AiIconBtn` (topbar.tsx),
 * including its two star paths. Conduit's reasoning is worth repeating because
 * it is what makes this read as a material catch-light rather than an alert:
 *
 *   "THE GLIMMER is a slow opacity + scale loop on the SMALL star only — a
 *    moving catch-light, which reads as material. Animating the whole mark would
 *    make the header pulse, and a pulsing header icon is a notification signal;
 *    this is not a notification. The 1.4s pause between cycles makes it twinkle
 *    rather than throb (a continuous cycle reads as a progress spinner)."
 *
 * Conduit drives this with Animated.sequence: 1100ms in, 1100ms out, then a
 * 1400ms delay — a 3600ms cycle. Reproduced here as CSS keyframes at the same
 * timings (1100/3600 = 30.6%, 2200/3600 = 61.1%, then held to 100%), with
 * ease-in-out to match Easing.inOut(Easing.quad). CSS rather than JS because
 * this runs on the compositor and costs nothing while the tab is open.
 *
 * WHAT DIFFERS FROM CONDUIT, deliberately: Conduit hardcodes its brand sky
 * because the point there is that the mark is always Conduit blue. This is a
 * different product, so the mark takes `--blue` from the active theme and stays
 * legible in both.
 *
 * ON HOVER, two further glints fade in and take up the same cycle offset from
 * each other, so the mark goes from a single catch-light to a small
 * constellation. They are `pointer-events: none` and purely decorative — nothing
 * about the button's behaviour changes, and none of it animates for anyone who
 * has asked for reduced motion.
 */

/** Conduit's four-pointed star and its companion spark, filled rather than stroked. */
const STAR_D = 'M12 2.6 13.75 9.1 20.25 10.85 13.75 12.6 12 19.1 10.25 12.6 3.75 10.85 10.25 9.1z';
const SPARK_D = 'M18.6 14.2 19.4 17 22.2 17.8 19.4 18.6 18.6 21.4 17.8 18.6 15 17.8 17.8 17z';
/** The two hover-only glints. Same shape, placed opposite the main spark. */
const GLINT_A_D = 'M5.2 3.4 5.8 5.5 7.9 6.1 5.8 6.7 5.2 8.8 4.6 6.7 2.5 6.1 4.6 5.5z';
const GLINT_B_D = 'M19.4 3.6 19.9 5.3 21.6 5.8 19.9 6.3 19.4 8 18.9 6.3 17.2 5.8 18.9 5.3z';

export function AiButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="util-btn ai-btn" onClick={onClick} title="Ask about your backups" aria-label="AI assistant">
      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
        <path className="ai-star" d={STAR_D} />
        <path className="ai-spark" d={SPARK_D} />
        <path className="ai-glint ai-glint-a" d={GLINT_A_D} />
        <path className="ai-glint ai-glint-b" d={GLINT_B_D} />
      </svg>
    </button>
  );
}
